import {
  HttpInterceptorFn,
  HttpHandlerFn,
  HttpRequest,
  HttpEvent,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { inject } from "@angular/core";
import { LoadingService } from "./core/services/loading.service";
import { ToastService } from "./core/services/toast.service";
import { QPilotAccessToken } from "./core/services/qpilot.service";

/**
 * Global HTTP interceptor for handling API requests
 * - Adds authentication headers
 * - Manages loading states
 * - Handles error responses
 */
export const apiInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  // Inject services using the inject function
  const loadingService = inject(LoadingService);
  const toastService = inject(ToastService);

  // Only start global loading if the custom header is present
  const showGlobalLoader = request.headers.has("X-Show-Global-Loader");
  if (showGlobalLoader) {
    loadingService.start();
  }

  // Remove the custom header before sending the request
  let modifiedRequest = request;
  if (showGlobalLoader) {
    modifiedRequest = modifiedRequest.clone({
      headers: modifiedRequest.headers.delete("X-Show-Global-Loader"),
    });
  }

  // Add authentication header (JWT) if available
  // Skip adding auth headers for Azure blob storage and other external resources
  // Also skip for payment gateway APIs that use their own authentication
  const skipAuthUrls = [
    "blob.core.windows.net",
    "fonts.googleapis.com",
    "fonts.gstatic.com",
    "clarity.ms",
    ".google.com/recaptcha", // reCAPTCHA API (matches www.google.com/recaptcha)
    "api.stripe.com", // Stripe API uses its own API key
    "apitest.authorize.net", // Authorize.Net test API uses its own credentials
    "api.authorize.net", // Authorize.Net production API uses its own credentials
  ];
  const shouldSkipAuth = skipAuthUrls.some((url) =>
    modifiedRequest.url.includes(url),
  );

  const authTokenStr = localStorage.getItem("session.accessToken");
  if (authTokenStr && !shouldSkipAuth) {
    try {
      const authToken = JSON.parse(authTokenStr) as QPilotAccessToken;
      if (authToken.TokenBearerAuth) {
        modifiedRequest = modifiedRequest.clone({
          setHeaders: {
            Authorization: `Bearer ${authToken.TokenBearerAuth}`,
          },
        });
      } else if (authToken.TokenAuth) {
        modifiedRequest = modifiedRequest.clone({
          setHeaders: {
            Authorization: `Basic ${authToken.TokenAuth}`,
          },
        });
      }
    } catch (error) {
      console.error("ApiInterceptor: Error parsing auth token:", error);
    }
  }

  function extractApiErrorMessage(apiError: unknown): string | undefined {
    if (
      typeof apiError === "object" &&
      apiError !== null &&
      !Array.isArray(apiError)
    ) {
      const errorObj = apiError as Record<string, unknown>;

      // Handle nested Messages.errors format (case-insensitive: Messages or messages)
      if (errorObj["Messages"] && typeof errorObj["Messages"] === "object") {
        const messages = errorObj["Messages"] as Record<string, unknown>;
        if (
          Array.isArray(messages["errors"]) &&
          messages["errors"].length > 0
        ) {
          return messages["errors"]
            .filter((error) => typeof error === "string")
            .join("; ");
        }
      }

      // Handle nested messages.errors format (lowercase)
      if (errorObj["messages"] && typeof errorObj["messages"] === "object") {
        const messages = errorObj["messages"] as Record<string, unknown>;
        if (
          Array.isArray(messages["errors"]) &&
          messages["errors"].length > 0
        ) {
          return messages["errors"]
            .filter((error) => typeof error === "string")
            .join("; ");
        }
      }

      // Handle direct errors array
      if (Array.isArray(errorObj["errors"]) && errorObj["errors"].length > 0) {
        return errorObj["errors"]
          .filter((error) => typeof error === "string")
          .join("; ");
      }

      // Handle existing error fields
      const errorFields = ["Message", "ErrorMessage", "message"];
      for (const field of errorFields) {
        if (
          Object.prototype.hasOwnProperty.call(errorObj, field) &&
          typeof errorObj[field] === "string"
        ) {
          return errorObj[field];
        }
      }
    }
    return undefined;
  }

  function isGenericHttpError(message: string): boolean {
    // Filter out generic HTTP error messages that aren't helpful to users
    const genericPatterns = [
      /^Http failure response for .+: \d+/, // "Http failure response for URL: STATUS"
      /^Unknown Error$/,
      /^0 Unknown Error$/,
      /^\d+$/, // Just status codes
    ];

    return genericPatterns.some((pattern) => pattern.test(message));
  }

  return next(modifiedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      const apiError = (error as { error: unknown }).error;
      const apiErrorMessage = extractApiErrorMessage(apiError);

      // Only show toast if there's a meaningful API error message
      // Don't show generic HTTP error messages like "Http failure response..."
      if (apiErrorMessage && !isGenericHttpError(apiErrorMessage)) {
        toastService.showError("Error", apiErrorMessage);
      }

      return throwError(() => error);
    }),
    finalize(() => {
      if (showGlobalLoader) {
        loadingService.stop();
      }
    }),
  );
};
